import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import StatusTag from "@/components/base/StatusTag";
import ClueCard from "@/components/base/ClueCard";
import { useSeo } from "@/hooks/useSeo";
import { clues } from "@/mocks/clues";

function ClueDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const clue = clues.find((item) => item.slug === slug);

  const related = useMemo(
    () => clues.filter((item) => item.categorySlug === clue?.categorySlug && item.slug !== slug).slice(0, 3),
    [clue, slug]
  );

  const jsonLd = useMemo(() => {
    if (!clue) return undefined;
    return [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: clue.title,
        description: clue.summary,
        datePublished: clue.date,
        dateModified: clue.date,
        inLanguage: "zh-CN",
        articleSection: clue.categoryName,
        keywords: clue.tags.join(","),
        image: clue.cover,
        author: { "@type": "Organization", name: "爆料平台" },
        publisher: { "@type": "Organization", name: "爆料平台" },
        mainEntityOfPage: `https://gregbennettguitars.com.cn/clue/${clue.slug}`,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://gregbennettguitars.com.cn/" },
          { "@type": "ListItem", position: 2, name: "线索归档", item: "https://gregbennettguitars.com.cn/clues" },
          {
            "@type": "ListItem",
            position: 3,
            name: clue.title,
            item: `https://gregbennettguitars.com.cn/clue/${clue.slug}`,
          },
        ],
      },
    ];
  }, [clue]);

  useSeo({
    title: clue ? `${clue.title} - ${clue.categoryName} | 爆料平台` : "线索不存在 | 爆料平台",
    description: clue ? clue.summary : "该线索可能已被移动或删除，请返回线索归档查看其他内容。",
    keywords: clue ? `${clue.categoryName},${clue.tags.join(",")},爆料平台,匿名爆料` : "爆料平台",
    path: `/clue/${slug ?? ""}`,
    type: "article",
    jsonLd,
  });

  if (!clue) {
    return (
      <div className="flex min-h-[50vh] w-full flex-col items-center justify-center px-4 py-20 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-file-unknow-line text-3xl" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没有找到这条线索</h1>
        <p className="mt-2 text-sm text-foreground-600">它可能已被移动或删除，你可以回到归档继续浏览。</p>
        <Link
          to="/clues"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          返回线索归档
        </Link>
      </div>
    );
  }

  return (
    <article className="w-full bg-background-50">
      <div className="border-b border-background-200 bg-background-100">
        <div className="w-full px-4 py-8 md:px-8 md:py-10">
          <Breadcrumbs
            items={[
              { label: "首页", to: "/" },
              { label: "线索归档", to: "/clues" },
              { label: clue.categoryName, to: `/category/${clue.categorySlug}` },
              { label: clue.title },
            ]}
          />
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Link
              to={`/category/${clue.categorySlug}`}
              className="whitespace-nowrap rounded-full bg-primary-100 px-3 py-1 text-xs font-label font-medium text-primary-700 transition-colors hover:bg-primary-200"
            >
              {clue.categoryName}
            </Link>
            <StatusTag status={clue.status} size="md" />
          </div>
          <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
            {clue.title}
          </h1>
          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-foreground-500 md:text-sm">
            <span className="flex items-center gap-1.5">
              <i className="ri-map-pin-line" />
              {clue.region}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" />
              提交于 {clue.date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line" />
              {clue.views.toLocaleString("zh-CN")} 次浏览
            </span>
          </div>
        </div>
      </div>

      <div className="w-full px-4 py-10 md:px-8 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-8">
            <div className="relative h-64 w-full overflow-hidden rounded-lg border border-background-200 bg-background-200 md:h-96">
              <img
                src={clue.cover}
                alt={`${clue.title} - 爆料平台线索配图`}
                title={`${clue.title} - ${clue.categoryName}`}
                className="h-full w-full object-top"
              />
            </div>

            <div className="mt-8 space-y-5">
              {clue.content.map((paragraph, index) => (
                <p key={index} className="text-sm leading-loose text-foreground-700 md:text-base">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-8 rounded-lg border-l-4 border-accent-400 bg-accent-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-information-line text-accent-600" />
                核实状态说明
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                当前状态为「{clue.status}」。页面内容为投稿人陈述与公开资料整理，不代表平台结论。如你是相关方，可通过
                <Link to="/contact" className="link-underline mx-1 font-medium text-primary-600">
                  联系入口
                </Link>
                提交补充说明或更正申请。
              </p>
            </div>

            <div className="mt-8 flex flex-wrap gap-2">
              {clue.tags.map((tag) => (
                <span
                  key={tag}
                  className="whitespace-nowrap rounded-md bg-secondary-100 px-3 py-1 text-xs font-label text-secondary-900"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-24 space-y-6">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">你也有类似材料？</h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  匿名提交，不必暴露身份。文字说清经过，截图与文件可作为补充说明。
                </p>
                <Link
                  to="/submit"
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-send-plane-fill" />
                  我要匿名爆料
                </Link>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">投稿提示</h2>
                <ul className="mt-3 space-y-3 text-sm text-foreground-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line mt-0.5 text-secondary-600" />
                    尽量写清时间、地点与经过
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line mt-0.5 text-secondary-600" />
                    涉及个人信息请先打码
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line mt-0.5 text-secondary-600" />
                    多方说法会被并列呈现
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mt-16 border-t border-background-200 pt-10">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              同分类下的其他线索
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item) => (
                <ClueCard key={item.slug} clue={item} />
              ))}
            </div>
          </section>
        )}
      </div>
    </article>
  );
}

export default ClueDetailPage;